import React, { useMemo } from 'react';
import ThemeObject, { ThemeProvider } from 'styled-components';

import Icon from '../../../../components/Icon/Icon';

import { SliderTabType, SpreadsheetSliderProps } from '../../types';

import {
  SpreadsheetSliderWrapper,
  SpreadsheetSliderHeader,
  SpreadsheetSliderToogle,
  SpreadsheetSliderTabs,
  SpreadsheetSliderTabButton,
  SpreadsheetSliderContent,
  SLIDER_SLIDING_ANIMATION_LENGTH_MS,
} from './slider-components';

import SliderErrors from '../SliderErrors/SliderErrors';
import SliderErrorsTabIcon from '../SliderErrors/SliderErrorsTabIcon';
import { ERRORS_TAB_ID } from '../../hooks/useSliderState';

const SpreadsheetSlider = (props: SpreadsheetSliderProps) => {
  const { spreadsheetWrapperRef, hotTableRef, contents = [], columns, state, showSliderErrorsTab } = props;
  const { open, activeTab, toggleOpen, switchTab } = state;

  const hotInstance = hotTableRef.current?.hotInstance;

  React.useEffect(() => {
    const wrapper = spreadsheetWrapperRef.current;

    if (!wrapper) {
      return;
    }

    wrapper.dataset.slider = open ? 'open' : 'closed';
    wrapper.dataset.animating = 'true';

    setTimeout(() => {
      delete wrapper.dataset.animating;
      if (hotInstance && !hotInstance.isDestroyed) {
        hotInstance.refreshDimensions();
      }
    }, SLIDER_SLIDING_ANIMATION_LENGTH_MS);
  }, [open, spreadsheetWrapperRef, hotInstance]);

  const tabs: Record<string, SliderTabType> = contents.reduce((acc, tab) => {
    acc[tab.id] = tab;
    return acc;
  }, {});

  const ActiveTab = useMemo(() => {
    const activeTabComponent = tabs[activeTab]?.component;
    if (!(activeTab === ERRORS_TAB_ID && showSliderErrorsTab) && activeTabComponent) {
      return activeTabComponent;
    }
    return () => <></>;
  }, [activeTab, showSliderErrorsTab, tabs]);

  return (
    <ThemeProvider theme={(spreadsheetTheme: typeof ThemeObject) => ({ ...spreadsheetTheme, open })}>
      <SpreadsheetSliderWrapper role="complementary" data-testid="spreadsheet-slider-wrapper">
        <SpreadsheetSliderHeader>
          <SpreadsheetSliderToogle onClick={toggleOpen} aria-label={open ? 'close slider' : 'open slider'}>
            <Icon type="backward" size="xxl" data-testid="backward" />
          </SpreadsheetSliderToogle>
          <SpreadsheetSliderTabs>
            <>
              {contents.map((tab: SliderTabType) => (
                <SpreadsheetSliderTabButton
                  key={tab.id}
                  onClick={() => switchTab(tab.id)}
                  theme={{ active: activeTab === tab.id && open }}
                  aria-label={tab.buttonLabel}
                >
                  <Icon type={tab.icon} size="xxl" data-testid={tab.icon} />
                </SpreadsheetSliderTabButton>
              ))}
              {showSliderErrorsTab && (
                <SliderErrorsTabIcon
                  onClick={() => switchTab(ERRORS_TAB_ID)}
                  active={activeTab}
                  open={open}
                  getErrors={props.callbacks.getAllErrors}
                  aria-label="slider errors tab"
                />
              )}
            </>
          </SpreadsheetSliderTabs>
          <SpreadsheetSliderToogle aria-label="toggle slider" onClick={toggleOpen} />
        </SpreadsheetSliderHeader>
        {open && (
          <SpreadsheetSliderContent>
            {activeTab === ERRORS_TAB_ID && showSliderErrorsTab ? (
              <SliderErrors
                key={activeTab}
                getDataAtCell={props.callbacks.getDataAtCell}
                setDataAtCell={props.callbacks.setDataAtCell}
                errors={props.callbacks.getAllErrors()}
                columns={columns}
                onSliderErrorsSubmit={props.onSliderErrorsSubmit}
              />
            ) : (
              <ActiveTab key={activeTab} selected={props.selected} {...props.callbacks} />
            )}
          </SpreadsheetSliderContent>
        )}
      </SpreadsheetSliderWrapper>
    </ThemeProvider>
  );
};

export { SpreadsheetSlider };
